import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { PageHeader, Panel } from "@/components/AppShell";
import { GoalGauge } from "@/components/GoalGauge";
import { BalanceChart } from "@/components/BalanceChart";
import { Button } from "@/components/ui/button";
import { Field, Choice } from "@/pages/Profile";
import { usePortfolio } from "@/context/ProfileContext";
import { errMsg } from "@/lib/api";
import { annualContribs, project, requiredMonthly } from "@/lib/finance";
import { effectiveGoal } from "@/lib/fers";
import { Link } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { usd } from "@/lib/format";

export default function Goals() {
  const { profile, saveProfile, dash } = usePortfolio();
  const [p, setP] = useState(null);
  useEffect(() => { if (profile) setP(profile); }, [profile]);
  const bal = dash?.totals?.total || 0;
  const calc = useMemo(() => {
    if (!p) return null;
    const proj = project(p, bal);
    const g = effectiveGoal(p);
    const target = g.target;
    const req = requiredMonthly(target, bal, proj.rate, proj.years);
    const actual = annualContribs(p, Number(p.salary) || 0, Number(p.age) || 0).total / 12;
    return { proj, target, g, req, actual, delta: proj.final.expected - target };
  }, [p, bal]);
  if (!p || !calc) return <p className="text-sm text-muted-foreground">Loading...</p>;
  const on = calc.delta >= 0;
  const save = async () => { try { await saveProfile(p); toast.success("Goal saved"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div>
      <PageHeader title="Goal Trajectory" subtitle="Set a nest egg or a monthly income target and see whether your current savings rate gets you there."
        actions={<Button className="rounded-full" onClick={save} data-testid="goal-save-button">Save goal</Button>} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Panel title="Your goal" testid="panel-goal-settings">
          <div className="space-y-4">
            <Choice label="Goal type" k="goal_mode" p={p} set={setP} options={[["nest_egg", "Target nest egg"], ["income", "Target monthly income"]]} />
            {p.goal_mode === "nest_egg" ? <Field label="Target nest egg ($)" k="target_nest_egg" p={p} set={setP} />
              : <><Field label="Monthly income ($)" k="target_monthly_income" p={p} set={setP} /><Field label="Safe withdrawal rate %" k="withdrawal_rate" p={p} set={setP} hint={`Implies a nest egg of ${usd(calc.g.base)}`} /></>}
            <Field label="By age" k="retire_age" p={p} set={setP} />
            <div className="rounded-md border border-border p-4 space-y-3" data-testid="goal-pension-credit">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="gp">Count pension & Social Security</Label>
                <Switch id="gp" checked={!!p.include_pension} onCheckedChange={(v) => setP({ ...p, include_pension: v })} data-testid="goal-include-pension-switch" />
              </div>
              {p.include_pension && calc.g.guaranteed ? (
                <dl className="text-xs space-y-1.5 font-mono-num">
                  <div className="flex justify-between"><dt className="text-muted-foreground">Base goal</dt><dd data-testid="goal-base">{usd(calc.g.base)}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">Guaranteed income at {p.retire_age}</dt><dd>{usd(calc.g.guaranteed.annual / 12)}/mo</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">Covers savings of</dt><dd className="text-emerald-600" data-testid="goal-credit">-{usd(calc.g.credit)}</dd></div>
                  <div className="flex justify-between font-semibold"><dt>Savings goal</dt><dd data-testid="goal-effective-target">{usd(calc.target)}</dd></div>
                </dl>
              ) : <p className="text-xs text-muted-foreground">Goal uses savings only.</p>}
              <Link to="/pension" className="text-xs text-cyan-700 dark:text-cyan-400 underline" data-testid="goal-pension-link">Adjust pension estimate</Link>
            </div>
          </div>
        </Panel>
        <Panel title="Readiness" className="lg:col-span-2" testid="panel-readiness">
          <div className="grid sm:grid-cols-[auto_1fr] gap-8 items-center">
            <GoalGauge ratio={calc.target ? calc.proj.final.expected / calc.target : 0} size={200} testid="goals-gauge" />
            <div className="space-y-4">
              <p className={`font-display text-xl sm:text-2xl font-semibold ${on ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`} data-testid="goal-delta-message">
                {on ? `On track: projected surplus of ${usd(calc.delta)}` : `Projected shortfall of ${usd(-calc.delta)} at age ${p.retire_age}`}
              </p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="glass p-4"><p className="text-xs text-muted-foreground">Required monthly savings</p><p className="font-mono-num text-lg font-semibold mt-1" data-testid="required-monthly">{usd(calc.req)}</p></div>
                <div className="glass p-4"><p className="text-xs text-muted-foreground">Actual monthly (you + match + IRA)</p><p className={`font-mono-num text-lg font-semibold mt-1 ${calc.actual >= calc.req ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`} data-testid="actual-monthly">{usd(calc.actual)}</p></div>
              </div>
              <p className="text-xs text-muted-foreground">Required rate assumes your blended {calc.proj.rate.toFixed(2)}% return over {calc.proj.years} years from today's {usd(bal)}.</p>
            </div>
          </div>
        </Panel>
        <Panel title="Projection cone" className="lg:col-span-3" testid="panel-cone">
          <BalanceChart projection={calc.proj.series} target={calc.target} height={360} />
          <div className="grid grid-cols-3 gap-3 mt-4 text-center text-sm">
            {[["Conservative", calc.proj.final.conservative, "text-amber-600"], ["Expected", calc.proj.final.expected, "text-cyan-600"], ["Optimistic", calc.proj.final.optimistic, "text-emerald-600"]].map(([k, v, c]) => (
              <div key={k}><p className="text-xs text-muted-foreground">{k}</p><p className={`font-mono-num font-semibold ${c}`} data-testid={`cone-${k.toLowerCase()}`}>{usd(v)}</p></div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
